import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { RoomFiltersService } from '@core/services/room-filters/room-filters.service';
import {
  changeRoomFilter,
  generateDisneyCluster,
  HotelAvailabilityActions,
  setDisneyCluster,
  setEmptyRoomsFilters,
  setFilteredRooms,
  setRoomsFilters,
} from './hotelAvailability.actions';
import {
  selectActiveRoomFilters,
  selectAllRooms,
} from '@store/hotel-availability/hotel-availability.selectors';
import { AppState } from '@store/app.reducer';
import { DisneyService, ReqService } from '@core/services';

@Injectable()
export class HotelAvailabilityEffects {
  constructor(
    private actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly roomFiltersService: RoomFiltersService,
    private readonly disneyService: DisneyService,
    private readonly contextService: ReqService,
  ) {}

  setFilters$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HotelAvailabilityActions.SET_HOTEL_AVAILABILITY),
      withLatestFrom(this.store.select(selectAllRooms)),
      map(([action, rooms]) => {
        if (this.contextService.isDisneyHotel()) {
          return setEmptyRoomsFilters();
        }
        const filters = this.roomFiltersService.generateFilters(
          rooms,
          (action as any).hotelAvailability,
        );
        return setRoomsFilters({ filters: Array.from(filters.values()) });
      }),
    );
  });

  setDisneyFilters$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(setDisneyCluster),
      withLatestFrom(this.store.select(selectAllRooms)),
      map(([action, rooms]) => {
        const filters = this.roomFiltersService.generateFilters(
          rooms,
          (action as any).hotelAvailability,
        );
        return setRoomsFilters({ filters: Array.from(filters.values()) });
      }),
    );
  });

  setFiltersPreconfigured$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(setRoomsFilters, changeRoomFilter),
      withLatestFrom(
        this.store.select(selectActiveRoomFilters),
        this.store.select(selectAllRooms),
      ),
      map(([, filters, allRooms]) => {
        if (filters.length === 0) {
          return setFilteredRooms({ rooms: allRooms });
        }
        const roomsFiltered = this.roomFiltersService.filterRooms(allRooms, filters);
        return setFilteredRooms({ rooms: roomsFiltered });
      }),
    );
  });

  generateDisneyCluster$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(generateDisneyCluster),
      withLatestFrom(this.store.select(selectAllRooms)),
      filter(() => this.contextService.isDisneyHotel()),
      map(([, clusters]) => {
        return setDisneyCluster({
          clusters: this.disneyService.generateRoomClusters(clusters),
        });
      }),
    );
  });
}
